import { CheckCircleIcon, EyeIcon } from '@heroicons/react/20/solid';
import { formatDate, formatDateTime } from '../../common/formatDateText';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { DocumentTextIcon } from '@heroicons/react/24/solid';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * @param {{ typeUser: string, typeUserCanValidate: string[], onOpenValidation: (paymentId: string) => void, onOpenProfile: (userId: string) => void }} options
 */
export const buildPaymentsColumns = ({
	typeUser,
	typeUserCanValidate,
	onOpenValidation,
	onOpenProfile,
}) => [
		{
			accessorKey: 'paymentFolio',
			id: 'paymentFolio',
			header: 'Folio de pago',
			size: 250,
			enableSorting: false,
			cell: ({ getValue }) => (
				<span className='inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-semibold text-slate-700'>
					<DocumentTextIcon className='h-4 w-4 text-slate-500' />
					{getValue() || '-'}
				</span>
			),
			meta: {
				label: 'Folio de pago',
				searchable: true,
				searchPlaceholder: 'Buscar folio...',
				excelHeaders: ['Folio'],
			},
		},
		{
			accessorKey: 'status',
			id: 'status',
			header: 'Estatus del pago',
			size: 150,
			enableSorting: false,
			cell: ({ getValue }) => {
				const v = getValue();
				return (
					<div className='flex w-full items-center justify-center'>
						<div
							className={classNames(
								v === 'validado'
									? 'bg-emerald-100 text-emerald-600'
									: v === 'pendiente'
										? 'bg-orange-100 text-orange-600'
										: v === 'rechazado'
											? 'bg-rose-100 text-rose-500'
											: '',
								'flex w-[120px] justify-center rounded-md py-1 text-[.8rem] font-bold uppercase',
							)}
						>
							<span>{v}</span>
						</div>
					</div>
				);
			},
			meta: { label: 'Estatus del pago', excelHeaders: ['Estatus Pago'] },
		},
		{
			accessorKey: 'matricula',
			id: 'matricula',
			header: 'Matrícula',
			size: 120,
			enableSorting: false,
			cell: ({ getValue, row }) => (
				<div className='flex w-full items-center justify-center'>
					<button
						type='button'
						onClick={() => onOpenProfile(row.original?.idUser)}
						disabled={!row.original?.idUser}
						className='font-semibold text-slate-600 underline hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50'
					>
						{getValue()}
					</button>
				</div>
			),
			meta: {
				label: 'Matrícula',
				searchable: true,
				searchPlaceholder: 'Buscar matrícula...',
				excelHeaders: ['Matricula'],
			},
		},
		{
			accessorKey: 'nameUser',
			id: 'nameUser',
			header: 'Estudiante',
			size: 300,
			enableSorting: false,
			cell: ({ getValue, row }) => {
				const name = getValue() || '-';
				const email = row.original?.email;
				return (
					<div className='min-w-0'>
						<p className='truncate text-sm font-semibold capitalize text-slate-700'>{name}</p>
						{email ? (
							<p className='truncate text-xs text-slate-500' title={email}>
								{email}
							</p>
						) : (
							<p className='truncate text-xs text-slate-400'>-</p>
						)}
					</div>
				);
			},
			meta: {
				label: 'Estudiante',
				searchable: true,
				searchPlaceholder: 'Buscar por nombre o correo...',
				excelHeaders: ['Nombre estudiante', 'Correo electronico'],
			},
		},
		{
			accessorKey: 'paymentDeadlineDate',
			id: 'paymentDeadlineDate',
			header: 'Fecha límite de pago',
			size: 220,
			enableSorting: false,
			cell: ({ getValue }) => (
				<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{formatDate(getValue())}</p>
			),
			meta: { label: 'Fecha límite de pago', excelHeaders: ['Fecha limite pago'] },
		},
		{
			id: 'account',
			header: 'Cuenta',
			accessorFn: (row) =>
				[row?.accountName, row?.accountNumber].filter(Boolean).join(' · ') || '-',
			size: 240,
			enableSorting: false,
			cell: ({ row }) => {
				const name = row.original?.accountName || '-';
				const number = row.original?.accountNumber;
				const icon = row.original?.accountIcon;
				return (
					<div className='flex min-w-0 items-start gap-2'>
						{icon ? (
							<img className='mt-0.5 h-7 w-7 shrink-0 rounded object-contain' src={icon} alt='' />
						) : null}
						<div className='min-w-0 flex-1'>
							<p className='truncate text-sm font-semibold uppercase text-slate-700'>{name}</p>
							<p className='truncate text-xs text-slate-500'>{number || '-'}</p>
						</div>
					</div>
				);
			},
			meta: {
				label: 'Cuenta',
				excelHeaders: ['Nombre cuenta', 'Numero cuenta'],
			},
		},
		{
			accessorKey: 'paymentType',
			id: 'paymentType',
			header: 'Tipo de pago',
			size: 150,
			enableSorting: false,
			cell: ({ getValue }) => {
				const v = getValue();
				if (!v) return null;
				return (
					<div className='flex w-full justify-center'>
						<p
							className={classNames(
								v === 'suscripcion' ? 'bg-sky-100 text-sky-500' : 'bg-purple-100 text-purple-500',
								'rounded-md px-3 py-1 text-center text-md font-bold capitalize',
							)}
						>
							{v}
						</p>
					</div>
				);
			},
			meta: { label: 'Tipo de pago', excelHeaders: ['Tipo pago'] },
		},
		{
			accessorKey: 'amount',
			id: 'amount',
			header: 'Monto del pago',
			size: 140,
			enableSorting: false,
			cell: ({ getValue }) => {
				const v = getValue();
				const n = Number(v);
				const showDash = v == null || v === '' || Number.isNaN(n) || n <= 0;
				return (
					<div className='flex w-full justify-center'>
						<p
							className={
								showDash
									? 'text-center text-sm text-slate-500'
									: 'rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'
							}
						>
							{showDash ? '-' : `$ ${v}`}
						</p>
					</div>
				);
			},
			meta: { label: 'Monto del pago', excelHeaders: ['Monto del pago'] },
		},
		{
			accessorKey: 'formatCreatedAt',
			id: 'formatCreatedAt',
			header: 'Fecha de registro del pago',
			size: 220,
			enableSorting: true,
			cell: ({ getValue }) => (
				<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{formatDateTime(getValue())}</p>
			),
			meta: { label: 'Fecha de registro del pago', excelHeaders: ['Fecha de pago'] },
		},
		{
			accessorKey: 'formatUpdatedAt',
			id: 'formatUpdatedAt',
			header: 'Última actualización',
			size: 220,
			enableSorting: false,
			cell: ({ getValue }) => (
				<p className='rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'>{formatDateTime(getValue())}</p>
			),
			meta: { label: 'Última actualización', excelHeaders: ['Fecha ultima actualizacion'] },
		},
		{
			accessorKey: 'formatPaymentReceiptDate',
			id: 'formatPaymentReceiptDate',
			header: 'Fecha de recibo de pago',
			size: 200,
			enableSorting: false,
			cell: ({ getValue }) => {
				const v = getValue();
				const text = v == null || v === '' ? '-' : formatDate(v) || '-';
				const isEmpty = text === '-';
				return (
					<p
						className={
							isEmpty
								? 'text-center text-sm text-slate-500'
								: 'rounded-md bg-slate-100 px-3 py-1 text-center text-sm text-slate-700 capitalize'
						}
					>
						{text}
					</p>
				);
			},
			meta: { label: 'Fecha recibo de pago', excelHeaders: ['Fecha recibo pago'] },
		},
		{
			accessorKey: 'validatedBy',
			id: 'validatedBy',
			header: 'Validado por',
			size: 220,
			enableSorting: false,
			cell: ({ getValue }) => <p className='truncate text-sm font-semibold capitalize text-slate-700'>{getValue() || '-'}</p>,
			meta: { label: 'Validado por', excelHeaders: ['Validado por'] },
		},
		{
			id: 'action',
			header: '',
			size: 150,
			enableSorting: false,
			cell: ({ row }) => {
				const status = row.original?.status;
				const id = row.original?.id;
				const canValidate = typeUserCanValidate.includes(typeUser);
				if (status === 'pendiente' && canValidate) {
					return (
						<div className='flex w-full justify-center gap-2'>
							<Tooltip title='Validar pago' placement='left'>
								<button
									type='button'
									onClick={() => onOpenValidation(id)}
									className='flex justify-center rounded border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 hover:bg-gray-300'
								>
									<CheckCircleIcon className='block h-5 w-5' aria-hidden='true' />
									<span className='text-sm font-semibold ml-2'>Validar</span>
								</button>
							</Tooltip>
						</div>
					);
				}
				if (status === 'validado' || status === 'rechazado') {
					return (
						<div className='flex w-full justify-center'>
							<Tooltip title='Ver detalle de pago' placement='left'>
								<button
									type='button'
									onClick={() => onOpenValidation(id)}
									className='flex justify-center rounded border border-gray-200 bg-gray-100 px-3 py-2 text-slate-700 hover:bg-gray-300'
								>
									<EyeIcon className='block h-5 w-5' aria-hidden='true' />
									<span className='text-sm font-semibold ml-2'>Detalle</span>
								</button>
							</Tooltip>
						</div>
					);
				}
				return null;
			},
			meta: { label: 'Acciones', enableExcel: false, sticky: 'right' },
		},
	];
